import React from "react";
import { Clock, ArrowRight, Calendar } from "lucide-react";

/**
 * Agenda Widget — design preview
 *
 * Three variants of the proposed "Next 3 today" widget for the dashboard
 * home page. Static, no API calls. Reviewed before any real integration.
 *
 * Route: /mockups/agenda-widget
 */

const PRIMARY = "#7C3AED";

const SAMPLE = [
  {
    id: "a1",
    time: "09:30",
    duration: 60,
    customer: "Sophie R.",
    service: "Balayage",
    staff: "Maya Chen",
    status: "confirmed",
    color: { bg: "#F3E8FF", border: "#C4B5FD" },
  },
  {
    id: "a2",
    time: "11:00",
    duration: 30,
    customer: "Mia T.",
    service: "Cut & Style",
    staff: "Aisha Patel",
    status: "pending",
    color: { bg: "#E0F2FE", border: "#7DD3FC" },
  },
  {
    id: "a3",
    time: "14:30",
    duration: 30,
    customer: "Olivia P.",
    service: "Gloss",
    staff: "Maya Chen",
    status: "confirmed",
    color: { bg: "#FCE7F3", border: "#F9A8D4" },
  },
];

const STATUS_DOT = {
  confirmed: "bg-sky-400",
  pending: "bg-amber-400",
  completed: "bg-green-400",
  cancelled: "bg-rose-400",
  no_show: "bg-rose-400",
};

const formatTime = (t) => {
  const [hh, mm] = t.split(":").map(Number);
  const ampm = hh >= 12 ? "PM" : "AM";
  const h12 = hh % 12 === 0 ? 12 : hh % 12;
  return `${h12}:${String(mm || 0).padStart(2, "0")} ${ampm}`;
};

const todayLabel = new Date().toLocaleDateString("en-US", {
  weekday: "long",
  day: "numeric",
  month: "long",
});

const WidgetShell = ({ title, subtitle, children, variantId }) => (
  <div
    className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden w-full max-w-md"
    data-testid={`agenda-widget-${variantId}`}
  >
    <div className="px-5 pt-5 pb-3 flex items-start justify-between gap-3">
      <div>
        <p className="text-xs font-bold tracking-[0.15em] text-slate-400 uppercase">
          {subtitle}
        </p>
        <h3 className="text-lg font-bold text-slate-900 mt-0.5">{title}</h3>
      </div>
      <a
        href="/dashboard/calendar"
        className="inline-flex items-center gap-1 text-sm font-semibold hover:underline"
        style={{ color: PRIMARY }}
      >
        View all <ArrowRight className="w-4 h-4" />
      </a>
    </div>
    {children}
  </div>
);

// ─── Variant A — Stacked pastel cards (matches Schedule view) ───
const VariantA = () => (
  <WidgetShell
    title={`Today's agenda`}
    subtitle={todayLabel.toUpperCase()}
    variantId="a"
  >
    <div className="px-5 pb-5 flex flex-col gap-2.5">
      {SAMPLE.map((a) => (
        <div
          key={a.id}
          className="rounded-xl p-3 border-l-4 transition-all hover:shadow-md cursor-pointer"
          style={{ backgroundColor: a.color.bg, borderColor: a.color.border }}
        >
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-semibold text-slate-500">
              {formatTime(a.time)}
            </span>
            <span
              className={`w-2 h-2 rounded-full ${STATUS_DOT[a.status]}`}
              title={a.status}
            />
          </div>
          <p className="font-bold text-slate-900 leading-tight">{a.customer}</p>
          <p className="text-sm text-slate-600 truncate">{a.service}</p>
          <p className="text-xs text-slate-400 mt-1">
            {a.staff} · {a.duration} min
          </p>
        </div>
      ))}
    </div>
  </WidgetShell>
);

// ─── Variant B — Timeline rail with brand-color time pills ───
const VariantB = () => (
  <WidgetShell
    title="Next up"
    subtitle={todayLabel.toUpperCase()}
    variantId="b"
  >
    <div className="px-5 pb-5">
      <div className="relative pl-9">
        {/* vertical rail */}
        <span
          className="absolute left-3.5 top-3 bottom-3 w-px"
          style={{ backgroundColor: `${PRIMARY}30` }}
        />
        {SAMPLE.map((a, i) => (
          <div key={a.id} className="relative pb-4 last:pb-0">
            <span
              className="absolute -left-[26px] top-1.5 w-3.5 h-3.5 rounded-full border-2 border-white shadow"
              style={{ backgroundColor: PRIMARY, top: "8px" }}
            />
            <div className="flex items-baseline gap-2">
              <span className="text-sm font-bold text-slate-900">
                {formatTime(a.time)}
              </span>
              <span className="text-xs text-slate-400">· {a.duration} min</span>
            </div>
            <p className="font-semibold text-slate-900 leading-tight mt-0.5">
              {a.customer}
            </p>
            <div className="flex items-center gap-2 mt-1">
              <span
                className="text-xs px-1.5 py-0.5 rounded-full font-semibold"
                style={{
                  backgroundColor: `${PRIMARY}15`,
                  color: PRIMARY,
                }}
              >
                {a.service}
              </span>
              <span className="text-xs text-slate-500">{a.staff}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  </WidgetShell>
);

// ─── Variant C — Hero "Next" + 2 upcoming below ───
const VariantC = () => {
  const next = SAMPLE[0];
  const rest = SAMPLE.slice(1);
  return (
    <WidgetShell
      title="Up next"
      subtitle={`${SAMPLE.length} APPOINTMENTS TODAY`}
      variantId="c"
    >
      {/* Hero card */}
      <div
        className="mx-5 mb-3 rounded-2xl p-4 border-l-4"
        style={{
          backgroundColor: next.color.bg,
          borderColor: next.color.border,
        }}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5 text-slate-700">
            <Clock className="w-4 h-4" />
            <span className="text-sm font-bold">{formatTime(next.time)}</span>
            <span className="text-xs text-slate-500">· {next.duration} min</span>
          </div>
          <span
            className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-white text-slate-700 shadow-sm"
            data-testid="agenda-next-badge"
          >
            Next
          </span>
        </div>
        <p className="text-xl font-extrabold text-slate-900 leading-tight">
          {next.customer}
        </p>
        <p className="text-sm text-slate-700 mt-0.5">{next.service}</p>
        <p className="text-xs text-slate-500 mt-1">with {next.staff}</p>
      </div>
      {/* Then list */}
      <div className="px-5 pb-5 flex flex-col divide-y divide-slate-100">
        {rest.map((a) => (
          <div key={a.id} className="flex items-center gap-3 py-3 first:pt-0">
            <div
              className="w-1 h-10 rounded-full flex-shrink-0"
              style={{ backgroundColor: a.color.border }}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-xs font-bold text-slate-500">
                  {formatTime(a.time)}
                </span>
                <span
                  className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[a.status]}`}
                />
              </div>
              <p className="font-semibold text-slate-900 truncate text-sm">
                {a.customer} · {a.service}
              </p>
              <p className="text-xs text-slate-400 truncate">{a.staff}</p>
            </div>
          </div>
        ))}
      </div>
    </WidgetShell>
  );
};

// ─── Empty state preview (shown in all variants when no appts) ───
const EmptyState = () => (
  <WidgetShell
    title="Today's agenda"
    subtitle={todayLabel.toUpperCase()}
    variantId="empty"
  >
    <div className="px-5 pb-6 text-center py-6">
      <div
        className="w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-3"
        style={{ backgroundColor: `${PRIMARY}10` }}
      >
        <Calendar className="w-6 h-6" style={{ color: PRIMARY }} />
      </div>
      <p className="text-sm font-semibold text-slate-700">
        No appointments today
      </p>
      <p className="text-xs text-slate-400 mt-1">
        Enjoy the breather. Tomorrow's agenda will show up here.
      </p>
    </div>
  </WidgetShell>
);

const AgendaWidgetMockup = () => {
  return (
    <div className="min-h-screen bg-slate-100 py-10 px-4 sm:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <p className="text-xs font-bold tracking-[0.15em] text-slate-400 uppercase">
            Design Preview
          </p>
          <h1 className="text-3xl font-extrabold text-slate-900 mt-1">
            Agenda widget — pick a direction
          </h1>
          <p className="text-slate-600 mt-2 max-w-2xl">
            Three variants of the "Next 3 today" widget proposed for the
            Dashboard home. All re-use the Schedule View pastel-card palette
            and brand color. Static preview only — no real data, no calls.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <p className="text-sm font-semibold text-slate-500 mb-2">
              A · Stacked pastel cards
            </p>
            <VariantA />
            <p className="text-xs text-slate-400 mt-2 leading-relaxed">
              Pure match with the Schedule view. Lowest cognitive load. Best
              if owners already know the Schedule visual language.
            </p>
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-500 mb-2">
              B · Brand-color timeline
            </p>
            <VariantB />
            <p className="text-xs text-slate-400 mt-2 leading-relaxed">
              Vertical rail with brand dots. Reads as "what's coming"
              chronologically. Tightest vertical footprint.
            </p>
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-500 mb-2">
              C · Hero "next" + 2 upcoming
            </p>
            <VariantC />
            <p className="text-xs text-slate-400 mt-2 leading-relaxed">
              The most glance-friendly: one big "Next" card so owners
              instantly see who's about to walk in, two compact rows below.
              My recommended pick.
            </p>
          </div>
        </div>

        {/* Empty state */}
        <div className="mt-12">
          <p className="text-sm font-semibold text-slate-500 mb-2">
            Empty state (all variants share this)
          </p>
          <EmptyState />
        </div>

        {/* Footer */}
        <div className="mt-12 p-5 rounded-2xl bg-white border border-slate-200">
          <p className="text-xs font-bold tracking-[0.15em] text-slate-400 uppercase mb-2">
            Behaviour spec
          </p>
          <ul className="text-sm text-slate-700 space-y-1.5">
            <li>
              • Pulls today's confirmed/pending appointments, sorts by time
              ASC, shows the next 3 starting from <em>now</em>.
            </li>
            <li>
              • Lives on <code>/dashboard</code> (home), above the existing
              stats. Hidden when no appointments today (empty state shown
              instead).
            </li>
            <li>
              • Card click opens the existing appointment-details modal.
              "View all" deep-links to <code>/dashboard/calendar</code> in
              Schedule mode.
            </li>
            <li>
              • Auto-rolls forward: once an appointment's end-time has
              passed, it drops off and the next one slides in.
            </li>
            <li>
              • Re-uses the Schedule View pastel palette + status-dot legend
              (no new design vocabulary).
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AgendaWidgetMockup;
